/**
 * Attaches every input producer to the play surface for the lifetime of the game.
 *
 * Both producers run at once — see `useActiveScheme` for why. Losing pointer lock pauses
 * the game, which is the only place input touches game state; everything else it does
 * goes into `inputState`.
 */

import { useEffect, type RefObject } from 'react';
import { inputState } from './inputState';
import { attachKeyboardMouse } from './producers/keyboardMouse';
import { attachTouch } from './producers/touch';
import { publishStick } from './stickView';
import { useGameStore } from '../store/gameStore';

type Options = {
  onPointerLockUnavailable?: () => void;
};

export function useInputProducers(
  surfaceRef: RefObject<HTMLElement | null>,
  { onPointerLockUnavailable }: Options = {},
): void {
  const dispatch = useGameStore((store) => store.dispatch);

  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) return;

    const detachKeyboardMouse = attachKeyboardMouse({
      state: inputState,
      surface,
      onPointerLockLost: () => dispatch('PAUSE'),
      onPointerLockUnavailable,
    });

    const detachTouch = attachTouch({
      state: inputState,
      surface,
      onStickChange: publishStick,
    });

    return () => {
      detachKeyboardMouse();
      detachTouch();
    };
  }, [surfaceRef, dispatch, onPointerLockUnavailable]);
}
